import React, { useEffect, useState, useCallback } from "react";
import { useParams, useOutletContext, useSearchParams } from "react-router-dom";
import { Pagination } from "@mui/material";
import styles from "./Category.module.css";
import sharedStyles from "../../styles/SharedLayout.module.css";
import { getCategoryPage } from "../../lib/getCategoryPage";
import CategorySliders from "./components/CategorySliders/CategorySliders";
import ProductGrid from "../../components/ProductGrid/ProductGrid";
import { getProductsByCategory } from "../../utils/meilisearchClient";
import { extractFiltersFromProducts } from "../../utils/filterUtils";
import type { CategoryData, MeilisearchProduct } from "../../types-var/product";
import type { SortOption } from "../../utils/meilisearchClient";
import Filters from "../../components/Filters/Filters";
import CategoryPageSkeleton from "./components/CategoryPageSkeleton/CategoryPageSkeleton";

const CategoryPage: React.FC = () => {
  const { categoryUrl } = useParams<{ categoryUrl: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [category, setCategory] = useState<CategoryData | null>(null);
  const [products, setProducts] = useState<MeilisearchProduct[]>([]);
  const [availableFilters, setAvailableFilters] = useState({});
  const [filterStyles, setFilterStyles] = useState({});
  const [filterCounts, setFilterCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentSort, setCurrentSort] = useState<SortOption>("popularity:desc");
  const { toggleMiniCart } = useOutletContext<{ toggleMiniCart: () => void }>();
  const [filters, setFilters] = useState<{ [key: string]: string[] }>({});
  const [totalPages, setTotalPages] = useState(1);
  const [totalHits, setTotalHits] = useState(0);

  const currentPage = parseInt(searchParams.get("page") || "1", 10);
  const HITS_PER_PAGE = 24;

  const [localPage, setLocalPage] = useState(currentPage);

  console.log("category.content:", category?.content);

  useEffect(() => {
    setLocalPage(currentPage);
  }, [currentPage]);

  const fetchProducts = useCallback(
    async (
      categoryData: CategoryData,
      currentFilters: typeof filters,
      page: number = localPage
    ) => {
      try {
        const searchOptions: any = {
          sort: [currentSort],
          page: page,
          hitsPerPage: HITS_PER_PAGE,
        };

        console.log(`Fetching category products for page: ${page}`);

        let filterConditions: string[] = [];

        filterConditions.push(
          `(categories.id = ${categoryData.id} OR categories.parent.id = ${categoryData.id} OR categories.parent.parent.id = ${categoryData.id})`
        );

        if (Object.keys(currentFilters).length > 0) {
          Object.entries(currentFilters)
            .filter(([_, values]) => values.length > 0)
            .forEach(([key, values]) => {
              if (key === "Categorías") {
                const categoryFilters = values.map(
                  (categoryId) => `categories.id = ${categoryId}`
                );
                if (categoryFilters.length > 0) {
                  filterConditions.push(`(${categoryFilters.join(" OR ")})`);
                }
              } else {
                const attributeFilters = values.map((value) => {
                  try {
                    const parsed = JSON.parse(value);
                    return `(attributes.name = "${key}" AND attributes.values.name = "${parsed.name}")`;
                  } catch {
                    const safeValue = value.replace(/"/g, '\\"');
                    return `(attributes.name = "${key}" AND attributes.values.name = "${safeValue}")`;
                  }
                });
                if (attributeFilters.length > 0) {
                  filterConditions.push(`(${attributeFilters.join(" OR ")})`);
                }
              }
            });
        }

        searchOptions.filter = filterConditions.join(" AND ");

        console.log("Search filters:", searchOptions.filter);

        if (!categoryUrl) throw new Error("Category URL is undefined");

        const results = await getProductsByCategory(
          {
            id: categoryData.id,
            url: categoryUrl || "",
            level: categoryData.level,
          },
          searchOptions
        );

        return results;
      } catch (error) {
        console.error("Error fetching products:", error);
        throw error;
      }
    },
    [localPage]
  );

  useEffect(() => {
    const fetchCategoryData = async () => {
      if (!categoryUrl) return;
      setLoading(true);
      try {
        const categoryData = await getCategoryPage(categoryUrl);
        setCategory(categoryData);

        setSearchParams(
          (prev) => {
            prev.set("page", "1");
            return prev;
          },
          { replace: true }
        );

        const results = await fetchProducts(categoryData, filters, 1);
        const {
          filters: extractedFilters,
          styles,
          counts,
        } = extractFiltersFromProducts(results.hits);

        setProducts(results.hits);
        setAvailableFilters(extractedFilters);
        setFilterStyles(styles);
        setFilterCounts(counts);
        setTotalPages(results.totalPages);
        setTotalHits(results.totalHits);
      } catch (error) {
        setError(
          error instanceof Error ? error.message : "Failed to load category"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchCategoryData();
  }, [categoryUrl, setSearchParams, fetchProducts]);

  useEffect(() => {
    if (!category) return;

    const updateProducts = async () => {
      try {
        setLoading(true);
        const results = await fetchProducts(category, filters, localPage);
        const {
          filters: extractedFilters,
          styles,
          counts,
        } = extractFiltersFromProducts(results.hits);

        setProducts(results.hits);
        setAvailableFilters(extractedFilters);
        setFilterStyles(styles);
        setFilterCounts(counts);
        setTotalPages(results.totalPages);
        setTotalHits(results.totalHits);
        setLoading(false);
      } catch (error) {
        console.error("Error updating products:", error);
        setLoading(false);
      }
    };

    updateProducts();
  }, [filters, currentSort, category, localPage, fetchProducts]);

  const handleFilterChange = (newFilters: typeof filters) => {
    setFilters(newFilters);

    setSearchParams(
      (prev) => {
        prev.set("page", "1");
        return prev;
      },
      { replace: true }
    );
  };

  const handleSortChange = (sortBy: SortOption) => {
    setCurrentSort(sortBy);

    setSearchParams(
      (prev) => {
        prev.set("page", "1");
        return prev;
      },
      { replace: true }
    );
  };

  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    if (_event) {
      _event.preventDefault();
    }

    setLocalPage(page);

    console.log(
      `Setting category page to ${page}, updating URL and fetching new data`
    );

    const url = new URL(window.location.href);
    url.searchParams.set("page", page.toString());

    window.history.pushState({}, "", url.toString());

    window.scrollTo(0, 0);
  };

  console.log("category.content:", category?.content);

  useEffect(() => {
    console.log(`Category page state changed to: ${currentPage}`);
  }, [currentPage]);

  console.log("category.content:", category?.content);

  if (loading) return <CategoryPageSkeleton />;
  if (error) return <div>Error: {error}</div>;
  if (!category) return <div>Category not found</div>;

  return (
    <div className="container">
      <div className={sharedStyles.pageContainer}>
        <h1 className={sharedStyles.pageTitle}>{category.name}</h1>

        {category.content && (
          <div className={styles.categorySlidersContainer}>
            <CategorySliders content={category.content} />
          </div>
        )}

        <div className={sharedStyles.mainContent}>
          <div className={sharedStyles.filtersColumn}>
            {/* Only show Filters in desktop view - ProductGrid handles mobile */}
            <div className={styles.desktopFiltersOnly}>
              <Filters
                availableFilters={availableFilters}
                filters={filters}
                filterStyles={filterStyles}
                setFilters={handleFilterChange}
                filterCounts={filterCounts}
                currentCategoryLevel={category?.level}
                isMobile={false}
              />
            </div>
          </div>
          <div className={sharedStyles.productsColumn}>
            <ProductGrid
              products={products}
              toggleMiniCart={toggleMiniCart}
              availableFilters={availableFilters}
              filterStyles={filterStyles}
              filterCounts={filterCounts}
              onSortChange={handleSortChange}
              currentSort={currentSort}
              totalHits={totalHits}
              categoryContext={{
                id: category.id,
                url: category.url,
                level: category.level,
              }}
              filters={filters}
              setFilters={handleFilterChange}
              onClearFilters={() => handleFilterChange({})}
            />
            {totalPages > 1 && (
              <div className={styles.pagination}>
                <Pagination
                  count={totalPages}
                  page={localPage}
                  onChange={handlePageChange}
                  color="primary"
                  size="large"
                  className="customPagination"
                  showFirstButton
                  showLastButton
                  siblingCount={1}
                  boundaryCount={1}
                />
                <div
                  style={{ marginTop: "10px", fontSize: "14px", color: "#666" }}
                >
                  Page {localPage} of {totalPages}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategoryPage;
